import React from 'react';
import { useThrottleLeading } from './useThrottleLeading';
import { PersistStorageKey } from '@benzinga/session';
import { extractFromSessionStorage } from '@benzinga/frontend-utils';
import type { ScannerConfig } from '@benzinga/scanner-config-manager';
const PERSIST_TIMEOUT = 1000;

type Serializer<T> = (config: T) => string;

export const saveToSessionStorage = <T>(location: PersistStorageKey, config: T, toSave?: Serializer<T>) => {
  window.sessionStorage.setItem(location, toSave ? toSave(config) : JSON.stringify(config));
};

export const useSaveLayoutToSessonStorage = <T>(
  location: PersistStorageKey,
  config: T,
  layoutLoaded: boolean,
  toSave?: Serializer<NonNullable<T>>,
) => {
  const PersistStorageKeySave = useThrottleLeading(saveToSessionStorage<NonNullable<T>>, PERSIST_TIMEOUT);
  const PrevConfig = React.useRef<T | undefined>(undefined);

  React.useEffect(() => {
    if (layoutLoaded && config && PrevConfig.current !== config) {
      PersistStorageKeySave(location, config as NonNullable<T>, toSave);
      PrevConfig.current = config;
    }
  }, [PersistStorageKeySave, config, layoutLoaded, location, toSave]);

  React.useEffect(() => {
    const handle = () => {
      if (layoutLoaded && config && PrevConfig.current !== config) {
        saveToSessionStorage(location, config as NonNullable<T>, toSave);
      }
    };
    window.addEventListener('beforeunload', handle);
    return () => window.removeEventListener('beforeunload', handle);
  }, [PersistStorageKeySave, PrevConfig, config, layoutLoaded, location, toSave]);
};

export const extractLayoutFromPersist = () => {
  const workspaces = extractFromSessionStorage(PersistStorageKey.workspaces);
  const widgets = extractFromSessionStorage(PersistStorageKey.widgets);
  const scannerConfigs = extractFromSessionStorage<ScannerConfig[]>(PersistStorageKey.scannerConfigs) ?? [];
  const settings = extractFromSessionStorage(PersistStorageKey.settings);
  const initialized = Boolean(extractFromSessionStorage(PersistStorageKey.initialized));
  const layoutVersion = Number(extractFromSessionStorage(PersistStorageKey.layoutVersion) ?? 0);

  if (!initialized || !layoutVersion) {
    return undefined;
  }

  return {
    layoutVersion,
    scannerConfigs,
    settings,
    widgets,
    workspaces,
  };
};
